import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ReceiptLong as ReceiptLongIcon,
  Medication as MedicationIcon,
  Biotech as BiotechIcon,
  Groups as GroupsIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell,
  Legend, Tooltip as RechartsTooltip,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import PainelNaoConfigurado from '../../components/saude/PainelNaoConfigurado';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import CircularScore from '../../components/saude/CircularScore';

// ─── Tipos ───

interface Totais {
  total_atendimentos: number;
  atend_com_medicamento: number;
  atend_com_exame: number;
  indice_medicamentos: number;
  indice_exames: number;
  total_prescricoes_medicamento: number;
  total_solicitacoes_exame: number;
}

interface PorUnidade {
  codunidade: string;
  nome: string;
  total: number;
  com_med: number;
  com_exame: number;
  indice_medicamentos: number;
  indice_exames: number;
}

interface PorMes {
  mes: string;
  total: number;
  indice_medicamentos: number;
  indice_exames: number;
}

interface TopItem {
  nome: string;
  qtd: number;
}

interface IndicePrescricoes {
  totais: Totais;
  por_unidade: PorUnidade[];
  por_mes: PorMes[];
  top_medicamentos: TopItem[];
  top_exames: TopItem[];
  periodo: { inicio: string; fim: string };
  gerado_em: string;
}

const REFRESH_MS = 60_000;
const COR_MED = '#1976d2';
const COR_EXA = '#7b1fa2';
const BAR_COLORS = ['#1976d2', '#43a047', '#ff9800', '#7b1fa2', '#00838f', '#e53935', '#6d4c41', '#546e7a'];

const toISO = (d: Date): string => d.toISOString().slice(0, 10);
const num = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');
const pct = (n?: number | null) => `${(n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** Periodo default: ultimos 30 dias. */
const hoje = new Date();
const trintaDiasAtras = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

// ─── KPI Card ───

const KpiCard: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={72} height={36} />
            : <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Card de Indice (gauge circular + percentual preciso) ───

const IndiceCard: React.FC<{
  titulo: string; indice: number; atendimentos: number; total: number; cor: string; icon: React.ReactNode; loading: boolean;
}> = ({ titulo, indice, atendimentos, total, cor, icon, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
        {loading
          ? <Skeleton variant="circular" width={96} height={96} />
          : <CircularScore value={Math.round(indice)} color={cor} size={96} />}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: cor }}>
            {icon} {titulo}
          </Typography>
          {loading ? <Skeleton width={120} height={28} /> : (
            <>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>{pct(indice)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {num(atendimentos)} de {num(total)} atendimentos
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Pagina ───

const SaudeIndicePrescricoes: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<IndicePrescricoes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [naoConfigurado, setNaoConfigurado] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // Filtros — periodo default: ultimos 30 dias
  const [dataInicio, setDataInicio] = useState(toISO(trintaDiasAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const fetchRef = useRef<() => void>(() => {});

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getIndicePrescricoes(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
        inicio: dataInicio,
        fim: dataFim,
      });
      setNaoConfigurado(result?.configurado === false);
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar o indice de prescricoes.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, dataInicio, dataFim]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar filtros
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  const handleDateChange = useCallback((di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  }, []);

  const totais = data?.totais;
  const porUnidade = data?.por_unidade || [];
  const porMes = data?.por_mes || [];
  const topMed = data?.top_medicamentos || [];
  const topExa = data?.top_exames || [];

  const UnidadeTooltip = useMemo(() => ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as PorUnidade;
    return (
      <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1 }}>
        <Typography variant="caption" fontWeight={600} display="block">{d.nome}</Typography>
        <Typography variant="caption" display="block">Atendimentos: {num(d.total)}</Typography>
        <Typography variant="caption" display="block" sx={{ color: COR_MED }}>Medicamentos: {pct(d.indice_medicamentos)} ({num(d.com_med)})</Typography>
        <Typography variant="caption" display="block" sx={{ color: COR_EXA }}>Exames: {pct(d.indice_exames)} ({num(d.com_exame)})</Typography>
      </Box>
    );
  }, [theme.palette.divider]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um municipio para visualizar o indice de prescricoes.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon color="primary" /> Indice de Prescricoes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong></>
              : 'Todas as unidades do municipio'}
            {' • Percentual de atendimentos com prescricao de medicamentos e exames'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado as ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Proxima atualizacao em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filtro de periodo */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={handleDateChange} />
        </CardContent>
      </Card>

      {naoConfigurado && <PainelNaoConfigurado />}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* Indices em destaque */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <IndiceCard titulo="Indice de Medicamentos" indice={totais?.indice_medicamentos ?? 0}
            atendimentos={totais?.atend_com_medicamento ?? 0} total={totais?.total_atendimentos ?? 0}
            cor={COR_MED} icon={<MedicationIcon fontSize="small" />} loading={loading} />
        </Grid>
        <Grid item xs={12} md={6}>
          <IndiceCard titulo="Indice de Exames" indice={totais?.indice_exames ?? 0}
            atendimentos={totais?.atend_com_exame ?? 0} total={totais?.total_atendimentos ?? 0}
            cor={COR_EXA} icon={<BiotechIcon fontSize="small" />} loading={loading} />
        </Grid>
      </Grid>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><KpiCard label="Atendimentos no periodo" value={num(totais?.total_atendimentos)} icon={<GroupsIcon />} color="#00838f" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Atendimentos com medicamento" value={num(totais?.atend_com_medicamento)} icon={<MedicationIcon />} color={COR_MED} loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Prescricoes de medicamento" value={num(totais?.total_prescricoes_medicamento)} icon={<AssignmentIcon />} color="#43a047" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Solicitacoes de exame" value={num(totais?.total_solicitacoes_exame)} icon={<BiotechIcon />} color={COR_EXA} loading={loading} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Indice por unidade */}
        <Grid item xs={12} lg={7}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Indice por unidade (%)</Typography>
              {loading ? <Skeleton variant="rectangular" height={340} /> : (porUnidade.length ? (
                <ResponsiveContainer width="100%" height={Math.max(340, porUnidade.length * 46)}>
                  <BarChart data={porUnidade} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis type="number" tick={{ fontSize: 11 }} unit="%" />
                    <YAxis type="category" dataKey="nome" width={160} tick={{ fontSize: 11 }} interval={0} />
                    <RechartsTooltip content={<UnidadeTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="indice_medicamentos" name="Medicamentos" fill={COR_MED} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="indice_exames" name="Exames" fill={COR_EXA} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem atendimentos no periodo selecionado.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>

        {/* Evolucao mensal */}
        <Grid item xs={12} lg={5}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Evolucao mensal do indice (%)</Typography>
              {loading ? <Skeleton variant="rectangular" height={340} /> : (porMes.length ? (
                <ResponsiveContainer width="100%" height={340}>
                  <LineChart data={porMes} margin={{ top: 8, right: 16, bottom: 4, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="%" />
                    <RechartsTooltip formatter={(v: any) => pct(Number(v))} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="indice_medicamentos" name="Medicamentos" stroke={COR_MED} strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="indice_exames" name="Exames" stroke={COR_EXA} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem dados no periodo selecionado.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>

        {/* Top medicamentos */}
        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Medicamentos mais prescritos</Typography>
              {loading ? <Skeleton variant="rectangular" height={300} /> : (topMed.length ? (
                <ResponsiveContainer width="100%" height={Math.max(300, topMed.length * 34)}>
                  <BarChart data={topMed} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="nome" width={200} tick={{ fontSize: 10 }} interval={0} />
                    <RechartsTooltip />
                    <Bar dataKey="qtd" name="Itens" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }}>
                      {topMed.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem prescricoes de medicamento no periodo.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>

        {/* Top exames */}
        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Exames mais solicitados</Typography>
              {loading ? <Skeleton variant="rectangular" height={300} /> : (topExa.length ? (
                <ResponsiveContainer width="100%" height={Math.max(300, topExa.length * 34)}>
                  <BarChart data={topExa} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="nome" width={200} tick={{ fontSize: 10 }} interval={0} />
                    <RechartsTooltip />
                    <Bar dataKey="qtd" name="Itens" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 11 }}>
                      {topExa.map((_, i) => <Cell key={i} fill={BAR_COLORS[(i + 3) % BAR_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Box sx={{ py: 6, textAlign: 'center' }}><Typography color="text.secondary">Sem solicitacoes de exame no periodo.</Typography></Box>)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Chip size="small" variant="outlined" sx={{ mt: 2 }}
        label={`Periodo: ${data?.periodo?.inicio ?? dataInicio} a ${data?.periodo?.fim ?? dataFim} • Indice = atendimentos com prescricao / total de atendimentos`} />
    </Box>
  );
};

export default SaudeIndicePrescricoes;
